import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Public-facing labels the user asked for → mapped to existing routes.
const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Collections" },
  { to: "/gallery", label: "Projects" },
  { to: "/testimonials", label: "Voices" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 bg-espresso transition-shadow duration-500 ${
        scrolled ? "shadow-[0_20px_60px_-30px_rgba(0,0,0,0.6)]" : ""
      }`}
      style={{ borderBottom: "1px solid rgba(180,154,106,0.18)" }}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 h-16 md:h-[76px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-2 group"
        >
          <span className="font-display font-medium text-warm-ivory text-xl md:text-[1.55rem] tracking-[0.32em] uppercase leading-none">
            Sutra
          </span>
          <span className="hidden sm:inline text-champagne font-display italic text-lg md:text-[1.35rem] tracking-tight leading-none">
            interio
          </span>
        </Link>

        {/* Center nav — visible on large screens */}
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.slice(1, 6).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `fine-uppercase text-[0.68rem] transition-colors duration-300 ${
                  isActive ? "text-champagne" : "text-warm-ivory/75 hover:text-warm-ivory"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <Link
            to="/book-consultation"
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center border border-champagne/70 text-champagne text-[0.68rem] fine-uppercase px-5 md:px-6 py-2.5 hover:bg-champagne hover:text-espresso transition-colors duration-300"
          >
            Private Consultation
          </Link>

          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
            data-testid="nav-account-btn"
            aria-label="Account"
            className="grid place-items-center w-10 h-10 text-warm-ivory/70 hover:text-champagne transition-colors"
          >
            <User size={18} strokeWidth={1.3} />
          </Link>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center gap-2 text-warm-ivory hover:text-champagne transition-colors p-2"
          >
            {open ? <X size={20} strokeWidth={1.4} /> : <Menu size={20} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key="menu-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-espresso lg:hidden"
            style={{ borderTop: "1px solid rgba(180,154,106,0.18)" }}
            data-testid="menu-panel"
          >
            <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-8 grid gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 + i * 0.03 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-display text-3xl md:text-4xl py-3 ${
                        isActive ? "text-champagne italic" : "text-warm-ivory/80 hover:text-warm-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
